// Pure logic of the finance screens, shared with the tests. Amounts are PLN net.

export type BilledMedium = "el" | "gas" | "water";

/** What a metering point is billed as (the host's FinanceService.BilledMedium); null when it is not billed. */
export function billedMedium(medium: string): BilledMedium | null {
  switch (medium) {
    case "el":
    case "pv":
    case "bess":
      return "el";
    case "gas":
    case "water":
      return medium;
    default:
      return null;
  }
}

/**
 * Prices a tariff of the medium asks for besides the price per zone: electricity has statutory fees, contracted power
 * and reactive energy; gas a capacity fee per kWh/h and a monthly fee (its conversion factor is asked separately);
 * water sewage and a monthly subscription.
 */
export const PRICE_FIELDS_OF: Record<BilledMedium, readonly string[]> = {
  el: [
    "qualityFee",
    "capacityFee",
    "renewableFee",
    "fixedPerKwMonth",
    "fixedPerMonth",
    "reactivePerKvarh",
    "tanPhiThreshold",
    "excessPerKw",
  ],
  gas: ["fixedPerKwMonth", "fixedPerMonth"],
  water: ["sewagePerM3", "fixedPerMonth"],
};

/** Invoice categories of the medium, in the order an invoice lists them ("other" takes the rest). */
export const CATEGORIES_OF: Record<BilledMedium, readonly string[]> = {
  el: ["energy", "distribution", "statutory", "reactive"],
  gas: ["energy", "distribution"],
  water: ["water", "sewage", "distribution"],
};

/** A year's plan spread over months: evenly, or in proportion to a profile (e.g. last year's costs). */
export function spreadBudget(
  total: number,
  profile?: readonly (number | null)[],
): number[] {
  const weights =
    profile && profile.length === 12 && profile.some((v) => (v ?? 0) > 0)
      ? profile.map((v) => Math.max(0, v ?? 0))
      : Array<number>(12).fill(1);
  const sum = weights.reduce((a, b) => a + b, 0);
  const months = weights.map(
    (w) => Math.round(((total * w) / sum) * 100) / 100,
  );
  // Rounding must not change the total: the last month takes the remainder.
  months[11] =
    Math.round((total - months.slice(0, 11).reduce((a, b) => a + b, 0)) * 100) /
    100;
  return months;
}

/** The difference of an invoice from the estimate, as the share the reader sees ("+2,4 %"). */
export function invoiceDifference(
  net: number,
  estimate: number,
): number | null {
  return estimate > 0 ? (net - estimate) / estimate : null;
}

/** Rows of an invoice that add up to its net amount; what is not split into categories is "other". */
export function invoiceRows(
  net: number,
  parts: Record<string, number | null>,
): { category: string; amount: number }[] {
  const rows = Object.entries(parts)
    .filter(([, v]) => v !== null && !Number.isNaN(v) && v !== 0)
    .map(([category, amount]) => ({ category, amount: amount! }));
  const rest =
    Math.round((net - rows.reduce((a, r) => a + r.amount, 0)) * 100) / 100;
  return rest !== 0 && !rows.some((r) => r.category === "other")
    ? [...rows, { category: "other", amount: rest }]
    : rows;
}

/** The zone after `zone` among the group's zones, for clicking through an hour's zone. */
export function nextZone(zones: readonly string[], zone: string): string {
  const i = zones.indexOf(zone);
  return zones[(i + 1) % zones.length] ?? zone;
}

/** Hours of a day grouped into runs of the same zone, for the zone strip: [from, to, zone]. */
export function zoneRuns(hours: readonly string[]): [number, number, string][] {
  const runs: [number, number, string][] = [];
  hours.forEach((z, h) => {
    const last = runs[runs.length - 1];
    if (last && last[2] === z) {
      last[1] = h + 1;
    } else {
      runs.push([h, h + 1, z]);
    }
  });
  return runs;
}
